import React, { useState, useRef } from 'react';
import { leadImportService, LeadImportData, ValidationResponse, BulkImportResponse } from '../../services/leadImportService';
import ImportPreview from './ImportPreview';

interface BulkLeadImportProps {
  onSuccess: (results: BulkImportResponse) => void;
  onCancel: () => void;
}

interface ImportStep {
  id: string;
  name: string;
  description: string;
  completed: boolean;
}

const BulkLeadImport: React.FC<BulkLeadImportProps> = ({ onSuccess, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [parsedLeads, setParsedLeads] = useState<LeadImportData[]>([]);
  const [validationResults, setValidationResults] = useState<ValidationResponse | null>(null);
  const [importResults, setImportResults] = useState<BulkImportResponse | null>(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const steps: ImportStep[] = [
    {
      id: 'upload',
      name: 'Upload CSV',
      description: 'Select and upload your CSV file with lead data',
      completed: false
    },
    {
      id: 'validate',
      name: 'Validate Data',
      description: 'Review and validate the imported data',
      completed: false
    },
    {
      id: 'preview',
      name: 'Preview & Import',
      description: 'Preview valid leads and start the import process',
      completed: false
    },
    {
      id: 'results',
      name: 'Import Results',
      description: 'Review import results and any errors',
      completed: false
    }
  ];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }

      setCsvFile(file);
      setError(null);

      // Read file content
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;

        try {
          const leads = leadImportService.parseCsvToLeads(text);
          setParsedLeads(leads);
          console.log(`📄 Parsed ${leads.length} leads from CSV`);
        } catch (parseError) {
          setError(parseError instanceof Error ? parseError.message : 'Failed to parse CSV file');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setLoading(true);
      await leadImportService.downloadImportTemplate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download template');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateData = async () => {
    if (parsedLeads.length === 0) {
      setError('No valid leads found in the CSV file');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log(`🔍 Validating ${parsedLeads.length} leads...`);
      const validation = await leadImportService.validateLeads(parsedLeads);
      setValidationResults(validation);

      console.log('✅ Validation completed:', validation.summary);
      setCurrentStep(1);
    } catch (err) {
      console.error('❌ Validation failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to validate leads');
    } finally {
      setLoading(false);
    }
  };

  const handleImportLeads = async () => {
    if (!validationResults || validationResults.validation.valid.length === 0) {
      setError('No valid leads to import');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const validLeads = validationResults.validation.valid.map(item => item.lead);
      console.log(`📥 Importing ${validLeads.length} valid leads...`);

      const results = await leadImportService.bulkImportLeads(validLeads, skipDuplicates);
      setImportResults(results);

      console.log('✅ Import completed:', results.results);
      setCurrentStep(3);

      // Call success callback after a brief delay
      setTimeout(() => {
        onSuccess(results);
      }, 2000);

    } catch (err) {
      console.error('❌ Import failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to import leads');
    } finally {
      setLoading(false);
    }
  };

  const resetImport = () => {
    setCurrentStep(0);
    setCsvFile(null);
    setParsedLeads([]);
    setValidationResults(null);
    setImportResults(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-between mb-8">
      {steps.map((step, index) => (
        <div key={step.id} className="flex items-center">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
            index <= currentStep
              ? 'bg-blue-600 border-blue-600 text-white'
              : 'border-gray-300 text-gray-500'
          }`}>
            {index < currentStep ? '✓' : index + 1}
          </div>
          <div className="ml-3">
            <div className={`text-sm font-medium ${
              index <= currentStep ? 'text-blue-600' : 'text-gray-500'
            }`}>
              {step.name}
            </div>
            <div className="text-xs text-gray-500">{step.description}</div>
          </div>
          {index < steps.length - 1 && (
            <div className={`ml-6 w-12 h-0.5 ${
              index < currentStep ? 'bg-blue-600' : 'bg-gray-300'
            }`} />
          )}
        </div>
      ))}
    </div>
  );

  const renderUploadStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload CSV File</h3>
        <p className="text-gray-600 mb-6">
          Upload a CSV file containing your lead data. You can download our template to get started.
        </p>
      </div>

      <div className="flex justify-center mb-6">
        <button
          onClick={handleDownloadTemplate}
          disabled={loading}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
        >
          {loading ? 'Downloading...' : '📥 Download CSV Template'}
        </button>
      </div>

      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          className="hidden"
          id="csv-upload"
        />
        <label htmlFor="csv-upload" className="cursor-pointer">
          <div className="text-4xl text-gray-400 mb-4">📄</div>
          <div className="text-lg font-medium text-gray-900 mb-2">
            {csvFile ? csvFile.name : 'Choose CSV file'}
          </div>
          <div className="text-sm text-gray-500">
            Click to browse or drag and drop your CSV file here
          </div>
        </label>
      </div>

      {csvFile && parsedLeads.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <span className="text-green-600">✅</span>
            <span className="text-green-700 font-medium">
              File loaded successfully! Found {parsedLeads.length} potential leads.
            </span>
          </div>
          <button
            onClick={handleValidateData}
            disabled={loading}
            className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Validating...' : 'Validate Data →'}
          </button>
        </div>
      )}
    </div>
  );

  const renderValidationStep = () => {
    if (!validationResults) return null;

    const { validation, summary } = validationResults;

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Validation Results</h3>
          <p className="text-gray-600">
            Review the validation results below and proceed with import
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{summary.valid}</div>
            <div className="text-sm text-blue-800">Valid Leads</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{summary.invalid}</div>
            <div className="text-sm text-red-800">Invalid Leads</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{summary.duplicates}</div>
            <div className="text-sm text-yellow-800">Duplicates</div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-orange-600">{summary.warnings}</div>
            <div className="text-sm text-orange-800">Warnings</div>
          </div>
        </div>

        {/* Import Options */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">Import Options</h4>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={skipDuplicates}
              onChange={(e) => setSkipDuplicates(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              Skip duplicate emails (recommended)
            </span>
          </label>
        </div>

        {/* Error Details */}
        {validation.invalid.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-medium text-red-900 mb-3">
              Invalid Leads ({validation.invalid.length})
            </h4>
            <div className="max-h-48 overflow-y-auto space-y-2">
              {validation.invalid.slice(0, 10).map((item, index) => (
                <div key={index} className="text-sm">
                  <span className="font-medium text-red-800">Line {item.lineNumber}:</span>
                  <span className="text-red-700 ml-2">{item.errors.join(', ')}</span>
                </div>
              ))}
              {validation.invalid.length > 10 && (
                <div className="text-sm text-red-600 italic">
                  ... and {validation.invalid.length - 10} more errors
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-4">
          <button
            onClick={() => setCurrentStep(2)}
            disabled={summary.valid === 0}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Continue with {summary.valid} Valid Leads →
          </button>
          <button
            onClick={resetImport}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Start Over
          </button>
        </div>
      </div>
    );
  };

  const renderPreviewStep = () => {
    if (!validationResults) return null;

    return (
      <ImportPreview
        validationResults={validationResults}
        onImport={handleImportLeads}
        onBack={() => setCurrentStep(1)}
        loading={loading}
        skipDuplicates={skipDuplicates}
        onSkipDuplicatesChange={setSkipDuplicates}
      />
    );
  };

  const renderResultsStep = () => {
    if (!importResults) return null;

    const { results } = importResults;

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Import Completed!</h3>
          <p className="text-gray-600">
            Your bulk import has been processed. Review the results below.
          </p>
        </div>

        {/* Results Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-green-600">{results.created}</div>
            <div className="text-sm text-green-800">Leads Created</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-yellow-600">{results.skipped}</div>
            <div className="text-sm text-yellow-800">Leads Skipped</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-red-600">{results.errors}</div>
            <div className="text-sm text-red-800">Errors</div>
          </div>
        </div>

        {/* Success Message */}
        {results.created > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <span className="text-green-600">🎉</span>
              <span className="text-green-700 font-medium">
                Successfully imported {results.created} leads! They have been automatically segmented and are ready for follow-up.
              </span>
            </div>
          </div>
        )}

        {/* Error Details */}
        {results.errors > 0 && results.details.errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-medium text-red-900 mb-3">
              Import Errors ({results.errors})
            </h4>
            <div className="max-h-48 overflow-y-auto space-y-2">
              {results.details.errors.slice(0, 10).map((error, index) => (
                <div key={index} className="text-sm">
                  <span className="font-medium text-red-800">Line {error.lineNumber}:</span>
                  <span className="text-red-700 ml-2">{error.email} - {error.error}</span>
                </div>
              ))}
              {results.details.errors.length > 10 && (
                <div className="text-sm text-red-600 italic">
                  ... and {results.details.errors.length - 10} more errors
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-4">
          <button
            onClick={() => onSuccess(importResults)}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            ✅ Done - View Leads
          </button>
          <button
            onClick={resetImport}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Import More Leads
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-screen overflow-y-auto">
        <div className="px-6 py-4">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Bulk Lead Import</h2>
              <p className="text-sm text-gray-500">Import multiple leads from a CSV file</p>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 text-2xl font-light transition-colors duration-200"
              disabled={loading}
            >
              ×
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
              <span className="text-red-600">❌</span>
              <span className="text-red-700 font-medium">{error}</span>
            </div>
          )}

          {/* Step Indicator */}
          {renderStepIndicator()}

          {/* Step Content */}
          <div className="min-h-[400px]">
            {currentStep === 0 && renderUploadStep()}
            {currentStep === 1 && renderValidationStep()}
            {currentStep === 2 && renderPreviewStep()}
            {currentStep === 3 && renderResultsStep()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkLeadImport;